// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import { Button } from '@imprivata-cloud/design-system';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createCodingCtxV2 } from '@/api/utils';
import {
  mapErrorCodeToStatus,
  OTP_CHALLENGE_STATUS,
  OtpChallenge,
  type OtpChallengeCopy,
  type OtpChallengeStatus,
} from '@/components/otpChallenge/OtpChallenge';
import { RootRoutes } from '@/index';
import useRequestEmailVerification from '@/self-service/hooks/useEmailVerification';
import useConfirmEmailOtp from '../../../hooks/useConfirmEmailOtp';
import OtpInstructions from './OtpInstructions';

import './Otp.less';

enum OtpStep {
  INSTRUCTIONS = 'INSTRUCTIONS',
  VERIFICATION = 'VERIFICATION',
}

const TRANSLATION_ROOT = 'self-service.auth-method.otp';

const COPY: OtpChallengeCopy = {
  [OTP_CHALLENGE_STATUS.SENT]: `${TRANSLATION_ROOT}.send-banner`,
  [OTP_CHALLENGE_STATUS.INCORRECT]: `${TRANSLATION_ROOT}.wrong-banner`,
  [OTP_CHALLENGE_STATUS.INVALIDATED]: `${TRANSLATION_ROOT}.invalidated-banner`,
  [OTP_CHALLENGE_STATUS.EXPIRED]: `${TRANSLATION_ROOT}.wrong-banner`,
  resendLink: `${TRANSLATION_ROOT}.resend-code`,
};

const Otp = () => {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();
  const navigateKeepSearch = useNavigateKeepSearch();
  const [step, setStep] = useState(OtpStep.INSTRUCTIONS);
  const [otpId, setOtpId] = useState('');
  const [status, setStatus] = useState<OtpChallengeStatus | null>(null);

  const { mutate: requestEmailVerification } = useRequestEmailVerification();
  const { mutate: confirmEmailOtp, isPending: isSubmitting } = useConfirmEmailOtp();

  const clearStatus = useCallback(() => setStatus(null), []);

  const requestOtp = useCallback(
    (onIssued: () => void) => {
      clearStatus();
      requestEmailVerification(undefined, {
        onSuccess: response => {
          setOtpId(response.otpId);
          setStatus(OTP_CHALLENGE_STATUS.SENT);
          onIssued();
        },
        onError: error => {
          console.error('Error requesting OTP:', error);
          emitError(new AppError(error as unknown as ApiError));
        },
      });
    },
    [requestEmailVerification, clearStatus, emitError],
  );

  const handleContinue = useCallback(() => {
    requestOtp(() => setStep(OtpStep.VERIFICATION));
  }, [requestOtp]);

  const handleResend = useCallback(() => {
    requestOtp(() => undefined);
  }, [requestOtp]);

  const handleSubmit = useCallback(
    (code: string) => {
      clearStatus();
      void (async () => {
        try {
          const codingCtx = await createCodingCtxV2();
          confirmEmailOtp(
            {
              codingCtxHeader: codingCtx.buildImprCodingCtxHeader(),
              confirmRequest: { otpId, otp: codingCtx.encryptString(code) },
            },
            {
              onSuccess: () => navigateKeepSearch(RootRoutes.SELF_SERVICE),
              onError: error => {
                const nextStatus = mapErrorCodeToStatus(error);
                if (nextStatus === null) {
                  emitError(new AppError(error as unknown as ApiError));
                } else {
                  setStatus(nextStatus);
                }
              },
            },
          );
        } catch (error) {
          emitError(new AppError(error as ApiError));
        }
      })();
    },
    [otpId, confirmEmailOtp, navigateKeepSearch, clearStatus, emitError],
  );

  const cancel = useCallback(() => {
    navigateKeepSearch(RootRoutes.SELF_SERVICE);
  }, [navigateKeepSearch]);

  return (
    <>
      {step === OtpStep.INSTRUCTIONS && <OtpInstructions onContinue={handleContinue} />}
      {step === OtpStep.VERIFICATION && (
        <div className="validation">
          <OtpChallenge
            heading={
              <>
                <h1 className="validation-title">{t(`${TRANSLATION_ROOT}.title-enter`)}</h1>
                <div className="validation-description">
                  {t(`${TRANSLATION_ROOT}.description-enter`)}
                </div>
              </>
            }
            groupLabel={t(`${TRANSLATION_ROOT}.title-enter`)}
            copy={COPY}
            statusVariant="banner"
            codeIssuedAtMs={null}
            status={status}
            onStatusClear={clearStatus}
            onSubmit={handleSubmit}
            onResend={handleResend}
            isSubmitting={isSubmitting}
            actions={({ canSubmit, submit }) => (
              <div className="validation-actions">
                <Button variant="primary" onClick={submit} disabled={!canSubmit}>
                  {t('common.verify')}
                </Button>
                <Button variant="text" onClick={cancel}>
                  {t('common.cancel')}
                </Button>
              </div>
            )}
          />
        </div>
      )}
    </>
  );
};

export default Otp;
